import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {BehaviorSubject, empty, Observable} from 'rxjs';
import {VetClinicStartContextData} from '../models/vet-clinic-start-context-data.model';
import {CommonUtils} from '../utils/common';
import {VetineoApiResponse} from '../models/vetineo-api-response.model';
import {delay, map, switchMap, take, tap} from 'rxjs/operators';
import {VetClinicData, VisitType} from '../models/vet-clinic-data.model';
import {fromPromise} from 'rxjs/internal-compatibility';
import {environment} from '../../environments/environment';
import {VetClinicInfoData} from "../models/vet-clinic-info-data.model";
import {VetClinic, VetDoctor} from "../models/vet-clinic.model";
import {Client} from "../models/client.model";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {VetClinicDgSearchResults} from "../models/datagrid-search-results/vet-clinic.dg-search-results.model";
import {Snapshot} from "../models/snpashot.model";
import {WidgetResponse} from "../models/widget-response.model";
import {Invoice} from "../models/invoice.model";
import {VetineoLicenceKeyResponse} from "../models/vetineo-licence-key-response.model";
import {VetineoLicenceStatus} from "../models/vetineo-licence-status.model";
import {AmbassadorLink} from "../models/ambassador-link.model";

/**
 * Główny serwis domenowy zarządzający placówkami weterynaryjnymi.
 * Pełni rolę magazynu stanu (State Management) dla aktualnego "Kontekstu Kliniki"
 * oraz dostarcza pełen zestaw operacji CRUD do zarządzania profilami placówek.
 */
@Injectable({
  providedIn: 'root'
})
export class VetClinicService implements DataGridServiceInterface<Observable<VetClinicDgSearchResults>> {
  /** Reaktywny strumień przechowujący globalne dane kontekstu startowego wybranej kliniki. */
  private _vetClinicStartContextDataSubject = new BehaviorSubject<VetClinicStartContextData>(null);

  /** Reaktywny strumień przechowujący ogólne informacje o wybranej klinice. */
  private _vetClinicInfoDataSubject = new BehaviorSubject<VetClinicInfoData>(null);

  /** Strumień wyzwalający sygnał do asynchronicznego odświeżenia danych kliniki w całej aplikacji (np. po komunikacie SSE). */
  private _vetClinicDataRefresherSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private apiService: ApiService,
    public commonUtils: CommonUtils
  ) {
  }


  /**
   * Pobiera paginowaną listę wszystkich klinik w systemie dla widoku administracyjnego DataGrid.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean): Observable<VetClinicDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        console.log(vetineoApiResponse)
        return new VetClinicDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje kliniki w oparciu o filtry.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<VetClinicDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/vet-clinics/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new VetClinicDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  get vetClinicDataRefresherSubject() {
    return this._vetClinicDataRefresherSubject.asObservable();
  }

  updateVetClinicDataRefresherSubject(newValue: boolean) {
    this._vetClinicDataRefresherSubject.next(newValue);
  }

  /**
   * Pobiera z pamięci RAM (lub odtwarza z LocalStorage) kontekst startowy wybranej kliniki.
   */
  getVetClinicStartContextData() {
    if (this._vetClinicStartContextDataSubject.getValue()) {
      return this._vetClinicStartContextDataSubject.asObservable();
    } else {
      return fromPromise(this.commonUtils.getStorageObject<VetClinicStartContextData>('vetClinicStartContext').then((vetClinicStartContextObj) => {
        return vetClinicStartContextObj;
      })).pipe(take(1), switchMap(vetClinicStartContext => {
        this._vetClinicStartContextDataSubject.next(vetClinicStartContext);
        return this._vetClinicStartContextDataSubject.asObservable();
      }));
    }
  }

  /**
   * Pobiera z pamięci RAM (lub odtwarza z IonicStorage) ogólne informacje o wybranej klinice.
   */
  getVetClinicInfoData() {
    if (this._vetClinicInfoDataSubject.getValue()) {
      return this._vetClinicInfoDataSubject.asObservable();
    } else {
      return fromPromise(this.commonUtils.getStorageObject<VetClinicInfoData>('vetClinicInfoData').then((vetClinicInfoDataObj) => {
        return vetClinicInfoDataObj;
      })).pipe(tap(vetClinicInfoData => {
        this._vetClinicInfoDataSubject.next(vetClinicInfoData);
        return this._vetClinicInfoDataSubject.asObservable();
      }));
    }
  }

  /**
   * Ustawia nowy kontekst kliniki w pamięci RAM i utrwala go w przeglądarce.
   */
  setVetClinicStartContextData(vetClinicStartContext: VetClinicStartContextData) {
    this._vetClinicStartContextDataSubject.next(vetClinicStartContext);
    this.storeVetClinicStartContextData(vetClinicStartContext);
  }

  setVetClinicInfoData(vetClinicInfoData: VetClinicInfoData) {
    this._vetClinicInfoDataSubject.next(vetClinicInfoData);
    this.storeVetClinicInfoData(vetClinicInfoData);
  }

  storeVetClinicStartContextData(vetClinicStartContext: VetClinicStartContextData) {
    this.commonUtils.setStorageObject('vetClinicStartContext', vetClinicStartContext);
  }

  storeVetClinicInfoData(vetClinicInfoData: VetClinicInfoData) {
    this.commonUtils.setStorageObject('vetClinicInfoData', vetClinicInfoData);
  }


  /**
   * Wywołuje endpoint zasilający startowy zestaw danych konfiguracyjnych dla danej placówki.
   */
  vetClinicStartContext(uuid: string): Observable<VetClinicStartContextData> {
    if (this.commonUtils.isEmpty(uuid)) {
      return empty();
    }
    return this.apiService.post<VetineoApiResponse>('/vet-clinics/context-data/start', {uuid}).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetClinicStartContextData>(new VetClinicStartContextData(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Pobiera podstawowe informacje o klinice.
   */
  fetchVetClinicInfoData(): Observable<VetClinicInfoData> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/info-data`).pipe(delay(environment.FAKE_REQUEST_DELAY), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetClinicInfoData>(new VetClinicInfoData(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Pobiera pełny model kliniki skojarzonej z aktualnie zalogowanym użytkownikiem.
   */
  getVetClinic(): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinic`).pipe(delay(environment.FAKE_REQUEST_DELAY), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.vetClinic) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response.vetClinic);
      }
    }));
  }

  /**
   * Pobiera model konkretnej kliniki na podstawie jej UUID.
   */
  getSpecificVetClinic(vetClinicUUID: string): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinic/${vetClinicUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response.vetClinic) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response.vetClinic);
      }
    }));
  }

  /**
   * Pobiera model konkretnej kliniki na podstawie jej kodu vetcode.
   */
  getSpecificVetClinicByVetcode(vetcodeId: number): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinic-by-vetcode/${vetcodeId}`).pipe(delay(environment.FAKE_REQUEST_DELAY), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      }
    }));
  }

  /**
   * Agregator danych rezerwacyjnych i usługowych dla danej placówki.
   * Implementuje zaawansowaną logikę mapowania (Grouping) dostępnych typów wizyt,
   * dzieląc je na klasyczne wizyty lub pakiety abonamentowe (Bundles).
   *
   * @param vetcodeId Unikalny numer identyfikacyjny placówki.
   */
  vetClinicData(vetcodeId: number): Observable<VetClinicData> {
    if (this.commonUtils.isEmpty(vetcodeId)) {
      return empty();
    }
    return this.apiService.get<VetineoApiResponse>(`/vet-clinics/${vetcodeId}/data`).pipe(delay(environment.FAKE_REQUEST_DELAY), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        if (vetineoApiResponse.ok && vetineoApiResponse.response) {
          let vetClinicData = this.commonUtils.bestTypedCopyEver<VetClinicData>(new VetClinicData(), vetineoApiResponse.response);
          vetClinicData.groupedVisitTypes = new Map();

          if (vetClinicData.useVetineoVisitTypes) {
            for (const visitType of vetClinicData.visitTypes) {
              let groupedVisitTypes: VisitType[] = [];

              if (vetClinicData.groupedVisitTypes.has(visitType.bundleUUID.trim())) {
                groupedVisitTypes = vetClinicData.groupedVisitTypes.get(visitType.bundleUUID.trim());
              }

              groupedVisitTypes.push(visitType);

              vetClinicData.groupedVisitTypes.set(visitType.bundleUUID.trim(), groupedVisitTypes);
            }
          } else {
            for (const visitType of vetClinicData.visitTypes) {
              let groupedVisitTypes: VisitType[] = [];

              if (vetClinicData.groupedVisitTypes.has(visitType.name.trim())) {
                groupedVisitTypes = vetClinicData.groupedVisitTypes.get(visitType.name.trim());
              }

              groupedVisitTypes.push(visitType);

              vetClinicData.groupedVisitTypes.set(visitType.name.trim(), groupedVisitTypes);
            }
          }
          return vetClinicData;
        }
      }
    }));
  }

  /**
   * Aktualizuje informacjne dane placówki.
   */
  updateVetClinicInfoData(vetClinicInfoData: VetClinicInfoData): Observable<VetClinicInfoData> {
    return this.apiService.put<VetineoApiResponse>(`/vet-clinics/${vetClinicInfoData.vetcodeId}/info-data`, vetClinicInfoData)
      .pipe(delay(environment.FAKE_REQUEST_DELAY), map(vetineoApiResponse => {
        if (vetineoApiResponse.ok && vetineoApiResponse.response) {
          return this.commonUtils.bestTypedCopyEver<VetClinicInfoData>(new VetClinicInfoData(), vetineoApiResponse.response);
        }
      }));
  }

  /**
   * Modyfikuje profil przypisanego lekarza.
   */
  updateVetDoctor(vetDoctor: VetDoctor) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/vet-doctors/edit/${vetDoctor.uuid}`, vetDoctor).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetDoctor>(new VetDoctor(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Modyfikuje główne ustawienia placówki. Rozróżnia wariant zapisu z modyfikacją
   * galerii zdjęć profilowych, optymalizując w ten sposób wielkość wysyłanego ładunku (Payload).
   */
  updateVetClinic(vetClinic: VetClinic, withGallery: boolean = true) {
    if (withGallery) {
      return this.apiService.put<VetineoApiResponse>(`/dashboard/vet-clinics/gallery-included/edit/${vetClinic.vetcodeId}/${vetClinic.uuid}`, vetClinic).pipe(map(vetineoApiResponse => {
        if (vetineoApiResponse.ok) {
          return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
        } else {
          return undefined;
        }
      }));
    } else {
      return this.apiService.put<VetineoApiResponse>(`/dashboard/vet-clinics/gallery-not-included/edit/${vetClinic.vetcodeId}/${vetClinic.uuid}`, vetClinic).pipe(map(vetineoApiResponse => {
        if (vetineoApiResponse.ok) {
          return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
        } else {
          return undefined;
        }
      }));
    }
  }

  /**
   * Pobiera dziennik zdarzeń audytowych (log modyfikacji) dla placówki.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Usuwa placówkę z systemu.
   */
  deleteVetClinic(vetCodeId: number): Observable<VetClinic> {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/vet-clinics/${vetCodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response.vetClinic);
      }
    }));
  }

  /**
   * Przywraca usuniętą wcześniej placówkę.
   */
  restoreVetClinic(vetCodeId: number): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/restore/${vetCodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response.vetClinic);
      }
    }));
  }

  /**
   * Inicjuje proces pobrania (migracji) nowej struktury placówki na podstawie vetCode.
   */
  import(vetCode: string): Observable<VetClinic> {
    return this.apiService.get<VetineoApiResponse>(`/vet-clinics/${vetCode}/import`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
      }
    }));
  }

  /**
   * Wykonuje synchronizację danych lekarzy weterynarii z danymi z Sitesbi.
   * @param vetCode Unikalny vetcode placówki.
   */
  syncSitesbiVetDoctorData(vetCode: number) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/sitesbi-vet-doctor-data/${vetCode}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response) {
        console.log(vetineoApiResponse.response);
      } else {
      }
    }));
  }

  /**
   * Wykonuje pełną synchronizację kartotek lekarzy z systemem KlinikiXP.
   * Aktualizuje uprawnienia, harmonogramy i dane kontaktowe lekarzy w placówce.
   */
  syncVetDoctorData(vetCode: number) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/sync-vet-doctor-data/${vetCode}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.response && vetineoApiResponse.response) {
        console.log(vetineoApiResponse.response);
      } else {
      }
    }));
  }

  /**
   * Sprawdza fizyczne istnienie kliniki w bazie danych na podstawie vetcode.
   */
  checkIfNotExist(vetcode: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/vet-clinics/check-exist/${vetcode}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetcode;
      }
    }));
  }

  /**
   * Wyszukiwarka tekstowa klinik ograniczona do kontekstu konkretnego klienta.
   */
  searchForClinicsByPhrase(vClientUUID: string, phrase: string) {
    return this.apiService.post<VetineoApiResponse>('/dashboard/clinics/by-phrase', {
      phrase: phrase,
      vClientUUID: vClientUUID
    }, {})
      .pipe(take(1), map(vetineoApiResponse => {
        if (!vetineoApiResponse.errors && vetineoApiResponse.response) {
          return this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response);
        } else {
          throw new Error(vetineoApiResponse.errors?.main);
        }
      }));
  }

  /**
   * Pobiera listę ostatnich faktur wystawionych dla danej kliniki.
   */
  getLastInvoices(uuid: string) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/inv/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Invoice(), [], vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Zwraca listę wszystkich placówek przypisanych do danego klienta.
   */
  listClinicsForClient(vClientUUID: string): Observable<VetClinic[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clinics/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VetClinic(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Przenosi (migruje) klinikę pomiędzy strukturami różnych klientów.
   */
  switchClient(vetClinic: VetClinic, vClientUUID: string): Observable<VetClinic> {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/vet-clinics/switch/${vClientUUID}`, vetClinic).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Inicjuje proces rejestracji kliniki w zewnętrznym systemie księgowym Fakturownia.pl.
   */
  registerInFakturownia(vetClinicUUID: string): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/fktr-create/${vetClinicUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.tmp;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Sprawdza saldo środków zgromadzonych na subkoncie Przelewy24.
   */
  checkP24AccountBalance(vetcodeId: number): Observable<string> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/p24-acc-balance/${vetcodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response.amount;
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera kod widgetu oraz instrukcję skorzystania z Vetineo dla danej lecznicy.
   */
  getWidgetAndInstruction(uuid: string): Observable<WidgetResponse> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinics/wg/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<WidgetResponse>(new WidgetResponse(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Aktualizuje ustawienia kliniki.
   */
  setClinicSettings(settings, uuid: string) {
    return this.apiService.put<VetineoApiResponse>(`/dashboard/vet-clinics/stts/${uuid}`, settings).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver<VetClinic>(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Weryfikuje, czy klinika zaakceptowała warunki integracji z Fakturownią.
   */
  checkIfFakturowniaAccepted(uuid: string): Observable<Client> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/clinics/fkt-accepted/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Zapisuje akceptację regulaminu Fakturowni przez aktualnie zalogowanego administratora.
   */
  acceptFakturowniaTerm(): Observable<Client> {
    return this.apiService.get<VetineoApiResponse>('/dashboard/fakturownia-accept').pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new Client(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera aktualny klucz licencyjny platformy Vetineo dla danej kliniki.
   */
  getVetineoLicenceKey(uuid: string): Observable<VetineoLicenceKeyResponse> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/l-check/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetineoLicenceKeyResponse(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Waliduje kompletność danych niezbędnych do wygenerowania klucza licencyjnego.
   */
  checkAllPrepareToGenerateLicenceKey(uuid: string): Observable<VetineoLicenceStatus> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/l-check/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetineoLicenceStatus(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Generuje i zapisuje nowy klucz licencyjny Vetineo dla placówki.
   */
  createNewVetineoLicenceKey(uuid: string): Observable<VetineoLicenceKeyResponse> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/l-create/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetineoLicenceKeyResponse(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Sprawdza stan środków na koncie dostawcy bramki SMS w lecznicy.
   */
  checkSmsServerBalance(vetcodeId: number): Observable<boolean> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/sms-balance/${vetcodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return true;
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Pobiera unikalny link polecający (Ref-link) dla programu Ambasador.
   */
  getAmbassadorLink(vClientUUID: string): Observable<AmbassadorLink> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/ambassador-link/${vClientUUID}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new AmbassadorLink(), vetineoApiResponse.response);
      } else {
        throw new Error(vetineoApiResponse.errors?.main);
      }
    }));
  }

  /**
   * Konfiguruje dane uwierzytelniające do bramki SMS
   * używanej przez klinikę do komunikacji z pacjentami.
   */
  updateSMSSerwerCredentials(vetClinicUUID: string, smsSerwerUser: string, smsSerwerPassword: string,
                             smsSerwerSender: string, smsSerwerEnabled: boolean, smsProvider: string, apiKey: string): Observable<VetClinic> {
    return this.apiService.put<VetineoApiResponse>('/dashboard/sms-server-update', {
      uuid: vetClinicUUID,
      smsSerwerUser: smsSerwerUser,
      smsSerwerPassword: smsSerwerPassword,
      smsSerwerSender: smsSerwerSender,
      smsSerwerEnabled: smsSerwerEnabled,
      apiKey: apiKey,
      smsProvider: smsProvider,

    }).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedCopyEver(new VetClinic(), vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę autoryzowanych nazw nadawców (Sender ID) dla bramki SMS kliniki.
   */
  getSenders(vetClinicUUID): Observable<string[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/sms-server-senders/${vetClinicUUID}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Inicjuje nowe typy wizyt Vetineo dla danej lecznicy.
   */
  startVetineoVisitTypes(uuid) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visits-types/start/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Uruchamia konfigurację typów wizyt Vetineo dla tej lecznicy.
   */
  startVetineoVisitTypesConfiguration(uuid) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vetineo-visits-types/config-start/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Usuwa profil lekarza w ramach danej placówki.
   */
  deleteDoctor(uuid: string): Observable<string> {
    return this.apiService.delete<VetineoApiResponse>(`/dashboard/vet-doctors/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok && vetineoApiResponse.message && vetineoApiResponse.message == 'DOCTOR_REMOVED') {
        return vetineoApiResponse.message;
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Generuje statystyczny raport CSV typów wizyt w lecznicy.
   */
  getVisitTypeReports(uuid: any) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/visit-types/report/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return vetineoApiResponse.response;
      } else {
        throw new Error(vetineoApiResponse.errors.main);
      }
    }));
  }
  /**
   * Aktualizuje konfigurację kategorii nowych typów wizyt Vetineo.
   */
  updateVetineoVisitTypeCategoriesForVetClinic(requestObj: {vetClinicUUID: string; vetineoVisitTypeCategoriesJson: string}) {

    return this.apiService.put<VetineoApiResponse>('/dashboard/vet-clinics/vetineo-visit-type-categories',requestObj).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return true
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera listę typów wizyt obsługiwanych przez konkretnego lekarza w danej klinice.
   */
  getDoctorVisitTypes(doctorId, vetcodeId): Observable<VisitType[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-doctor-visit-types/${doctorId}/${vetcodeId}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new VisitType(), [], vetineoApiResponse.response);
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }

  /**
   * Zmienia status konta kliniki (np. na 'SUSPENDED').
   */
  changeClinicStatus(statusInfo) {
    return this.apiService.put<VetineoApiResponse>('/dashboard/vet-clinic-account-status', statusInfo).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return "OK";
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));  }

  /**
   * Przełącza widoczność kliniki na publicznych listach wyszukiwania.
   */
  changeHideFromList(uuid) {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/vet-clinic-hide-from-list/${uuid}`).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return "OK";
      } else {
        throw Error(vetineoApiResponse.errors.main);
      }
    }));
  }
}
